import React, { useEffect, useState } from "react";

import { useMediaQuery, Card } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import classnames from "classnames";

import {
  useListContext,
  BulkActionsToolbar,
  // Content,
  CreateButton,
  Datagrid,
  DateField,
  DateInput,
  EditButton,
  FilterButton,
  FunctionField,
  ListContextProvider,
  ListToolbar,
  RefreshButton,
  SelectField,
  SelectInput,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
} from "react-admin";

import { isSubscriptionDisabled } from "../../../utils/appUtils";
import { ChevronRightIcon } from "../../../components/common/iconography";
import { Empty } from "../../../components/common/Empty";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { SearchField } from "../../../components/common/SearchField";
import { Pagination } from "../../../components/Pagination";
import { useSearchableList } from "../../../dataProvider/useSearchableList";
import { BulkDeepDeleteButton } from "./BulkDeepDeleteButton";
import { FileVisibilityOptions } from "../../../utils/constants";

const useStyles = makeStyles((theme) => ({
  listRoot: {
    transition: theme.transitions.create(["all"], {
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginTop: 0,
  },
  bulkActions: {
    // [classes.bulkActions] remove to restore original behavior
    marginTop: -theme.spacing(8),
    transition: theme.transitions.create("margin-top"),
  },
  actions: {
    zIndex: 2,
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },
}));

const FileSearchField = (props) => {
  const listContext = useListContext(props);
  const { displayedFilters, filterValues, setFilters } = listContext;
  const { label, alwaysOn } = props;

  const handleSearch = (value) => {
    debugger;
    if (value) {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(
        {
          ...nextFilterValues,
          or: [
            { displayName: { matchPhrasePrefix: `${value}` } },
            { displayName: { wildcard: `*${value}*` } },
            { rawFileName: { matchPhrasePrefix: `${value}` } },
            { rawFileName: { wildcard: `*${value}*` } },
            { description: { matchPhrasePrefix: `${value}` } },
          ],
        },
        displayedFilters
      );
    } else {
      const nextFilterValues = { ...filterValues };
      delete nextFilterValues.or;
      setFilters(nextFilterValues, displayedFilters);
    }
  };

  return (
    <SearchField
      id="searchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
    />
  );
};

const getListFilters = (isMedium) => [
  <FileSearchField label="Search" alwaysOn />,
  <SelectInput
    source="category.eq"
    label="Category"
    variant="outlined"
    resettable
    alwaysOn={!isMedium}
    choices={window.appUser?.reloadYard?.fileCategories || []}
    optionValue="key"
  />,
  <DateInput
    source="contentDate.lte"
    label="Last Modified before..."
    variant="outlined"
    resettable
    onKeyPress={(event) => event.preventDefault()}
  />,
];

const BulkListActions = (props) => <BulkDeepDeleteButton {...props} />;

const ListActions = (props) => {
  const { isXSmall, basePath } = props;

  return !isXSmall ? (
    <TopToolbar>
      <FilterButton />
      <CreateButton basePath={basePath} disabled={isSubscriptionDisabled()} />
      <RefreshButton color="secondary" />
    </TopToolbar>
  ) : null;
};

export const FileList = (props) => {
  const classes = useStyles();
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const isMedium = useMediaQuery((theme) => theme.breakpoints.down("md"));

  // Example:
  // reloadYardId: window.appUser.reloadYardId
  // statusDueDate: { beginsWith: {status: 'NEW'} },
  const [defaultFilter] = useState({
    reloadYardId: window.appUser.reloadYardId,
  });

  // Example:
  // reloadYardId: { eq: window.appUser.reloadYardId }
  // status: {eq: 'STORED'}
  const defaultSearchFilter = {
    reloadYardId: { eq: window.appUser.reloadYardId },
  };

  // Include key & search to display default sort order.
  const defaultSort = {
    field: `{"key":"filesByName", "search":"displayName"}`,
    order: "ASC",
  };

  // memorize perPage settings
  // see useEffect [listContext?.perPage]) below
  const sessionPerPage = localStorage.getItem(`${props.resource}_perPage`);
  const [defaultPerPage, setDefaultPerPage] = useState(
    sessionPerPage ? Number(sessionPerPage) : 10
  );

  const listContext = useSearchableList({
    ...props,
    basePath: "/files",
    defaultTitle: "Documents",
    defaultSearchFilter,
    defaultFilter,
    defaultPerPage,
    defaultSort,
    resource: "files",
  });

  useEffect(() => {
    if (listContext?.perPage) {
      localStorage.setItem(`${props.resource}_perPage`, listContext.perPage);
      setDefaultPerPage(Number(listContext.perPage));
    }
  }, [listContext?.perPage]);

  useEffect(() => {
    if (listContext?.filter) {
      // Extract the key query from defaultSort
      const fieldObj = JSON.parse(defaultSort.field);
      const keyToCheck = fieldObj.key;

      // Check for the key's presence in filter
      const hasKey = keyToCheck in listContext.filter;
      const hasFilterValues = Object.keys(listContext.filterValues).length > 0;
      if (hasKey && hasFilterValues) listContext.setFilters({});
    }
  }, [listContext?.filter]);

  return (
    <ListContextProvider value={listContext}>
      <Title title={<ForceTitle title="Documents" />} />
      <ListToolbar
        {...props}
        filters={getListFilters(isMedium)}
        actions={<ListActions isXSmall={isXSmall} />}
      />
      <Card
        className={classnames(classes.listRoot, {
          [classes.bulkActions]: listContext.selectedIds.length > 0,
          // [classes.bulkActions] remove to restore original behavior
        })}
      >
        <BulkActionsToolbar>
          <BulkListActions />
        </BulkActionsToolbar>
        <FileGrid {...listContext} isXSmall={isXSmall} isMedium={isMedium} />
        <Pagination {...listContext} />
      </Card>
    </ListContextProvider>
  );
};

const categoryRender = (record) => {
  const category = window.appUser?.reloadYard?.fileCategories?.find(
    (itm) => itm.key === record.category
  );
  return category ? category.name : "";
};

const FileGrid = ({ ids, page, ...props }) => {
  const { isXSmall, isMedkum } = props;
  const hasBulkActions = true;

  if (ids?.length === 0 && page === 1)
    return <Empty {...props} message="Upload a new document" />;
  else
    return isXSmall ? (
      <SimpleList
        primaryText={(record) => record.displayName}
        secondaryText={(record) => `Category: ${categoryRender(record)}`}
        tertiaryText={(record) =>
          new Date(record.contentDate).toLocaleDateString()
        }
        // leftIcon={record => <></>}
        rightIcon={() => (
          <ChevronRightIcon
            fontSize="large"
            color="secondary"
            style={{ marginLeft: 40, marginTop: 10 }}
          />
        )}
        linkType="edit"
      />
    ) : isMedkum ? (
      <Datagrid rowClick="edit" hasBulkActions={hasBulkActions}>
        <TextField
          source="displayName"
          label="Name"
          sortable={true}
          sortBy={`{"key":"filesByName", "search":"displayName"}`}
        />
        <DateField source="contentDate" label="Modified" sortable={true} />
        <FunctionField
          source="category"
          label="Category"
          render={categoryRender}
          sortable={true}
        />
      </Datagrid>
    ) : (
      <Datagrid rowClick="edit" hasBulkActions={hasBulkActions}>
        <TextField
          source="displayName"
          label="Name"
          sortable={true}
          sortBy={`{"key":"filesByName", "search":"displayName"}`}
        />
        <DateField source="updatedAt" label="Updated" sortable={false} />
        <FunctionField
          source="category"
          label="Category"
          render={categoryRender}
          sortable={true}
        />
        <SelectField
          source="visibility"
          choices={FileVisibilityOptions}
          label="Visible To"
          sortable={true}
        />
        <EditButton />
      </Datagrid>
    );
};
